// People & organisations stream (ADR-028): person link review and affiliations on the person page
// (PB-03-04, PB-03-03), the reassignment action (PB-03-02), group roster import on the visit page
// (PB-04-03) and the collection matrix (PB-16-03). Every action is re-authorised by the API.
import type {
  AffiliationDto,
  AffiliationListResponse,
  ApplyRosterResponse,
  CollectionMatrixResponse,
  CollectionMatrixVersionDto,
  EffectiveCollectionResponse,
  HostResponse,
  HostReassignmentDto,
  InspectRosterResponse,
  OrganisationSummaryDto,
  PersonLinkDto,
  PersonLinksResponse,
  PossibleMatchesResponse,
  ReassignHostResponse,
  RosterImportDto,
  RosterRowDto,
  VisitDetailResponse,
} from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { ApiRequestError, api, get, newIdempotencyKey } from '../api.ts';
import { useI18n } from '../i18n.tsx';
import { useSession } from '../session.tsx';
import { Dialog, ErrorState, ErrorSummary, Field, Loading } from './ui.tsx';
import { MatrixPublishDialog } from './priv-ui.tsx'; // B-31 publish confirmation (PD-615)

type Mode = 'required' | 'optional' | 'disallowed';
const MODE_TONE: Record<Mode, string> = { required: 'blue', optional: '', disallowed: 'red' };

/** One idempotency key per opened form; a retry of the same intent reuses it. */
function useKey() {
  const [key] = useState(newIdempotencyKey);
  return key;
}

const today = () => new Date().toISOString().slice(0, 10);

// ------------------------------------------------------------------ collection modes (PB-16-03)
/** What the visit form may collect for a visitor type (evaluated by the API before rendering). */
export function useCollectionModes(siteId: string | null | undefined, categoryCode: string | null | undefined) {
  const { scopeKey } = useSession();
  const q = useQuery({
    queryKey: [...scopeKey, 'collection-effective', siteId, categoryCode ?? ''],
    enabled: !!siteId,
    queryFn: () => get<EffectiveCollectionResponse>(`/api/v1/collection-matrix/effective?${new URLSearchParams({ siteId: siteId!, ...(categoryCode ? { categoryCode } : {}) })}`),
    staleTime: 60_000,
  });
  const mode = (item: string): Mode => q.data?.items[item]?.mode ?? 'optional';
  return { mode, ready: !q.isPending };
}

// ------------------------------------------------------------------ person links (PB-03-04)
export function PersonLinksPanel({ personId }: { personId: string }) {
  const { t, tk, dateTime } = useI18n();
  const { scopeKey } = useSession();
  const matches = useQuery({ queryKey: [...scopeKey, 'person', personId, 'matches'], queryFn: () => get<PossibleMatchesResponse>(`/api/v1/people/${personId}/possible-matches`) });
  const links = useQuery({ queryKey: [...scopeKey, 'person', personId, 'links'], queryFn: () => get<PersonLinksResponse>(`/api/v1/people/${personId}/links`) });
  const [review, setReview] = useState<PossibleMatchesResponse['items'][number] | null>(null);
  const [reversing, setReversing] = useState<PersonLinkDto | null>(null);
  return (
    <section className="panel" aria-labelledby="orgs-matches">
      <div className="panel-head">
        <div>
          <h2 id="orgs-matches">{t('orgs.links.title')}</h2>
          <p>{t('orgs.links.sub')}</p>
        </div>
      </div>
      <div className="pad">
        <div className="note" role="note">
          {t('orgs.links.notProof')}
        </div>
        {matches.isPending ? (
          <Loading />
        ) : matches.isError ? (
          <ErrorState error={matches.error} />
        ) : matches.data.items.length === 0 ? (
          <p className="tiny">{t('orgs.links.none')}</p>
        ) : (
          <div className="tablewrap" tabIndex={0} aria-label={t('orgs.links.title')}>
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('orgs.links.col.profile')}</th>
                  <th scope="col">{t('orgs.links.col.reasons')}</th>
                  <th scope="col">{t('users.col.action')}</th>
                </tr>
              </thead>
              <tbody>
                {matches.data.items.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <Link to={`/people/${m.id}`}>
                        <strong>{m.displayName}</strong>
                      </Link>
                      <small>
                        {m.organisation ? `${m.organisation} · ` : ''}
                        <span data-ltr>{[m.maskedEmail, m.maskedPhone].filter(Boolean).join(' · ') || t('common.none')}</span>
                      </small>
                      {m.linkedProfiles ? <small>{t('orgs.links.linkedProfiles', { count: m.linkedProfiles })}</small> : null}
                    </td>
                    <td>{m.reasons.map((r) => tk(`orgs.links.reason.${r}`)).join(', ')}</td>
                    <td>
                      {matches.data.canDecide ? (
                        <button className="button small" type="button" onClick={() => setReview(m)}>
                          {t('orgs.links.review')}
                        </button>
                      ) : (
                        <small>{t('orgs.links.stewardOnly')}</small>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {links.data && links.data.items.length > 0 ? (
          <>
            <h3 style={{ marginBlockStart: 18 }}>{t('orgs.links.decisions')}</h3>
            <ul className="problems">
              {links.data.items.map((l) => (
                <li key={l.id}>
                  <strong>{tk(`orgs.links.decision.${l.decision}`)}</strong>{' '}
                  <span className={`badge ${l.status === 'active' ? 'green' : ''}`}>{tk(`orgs.links.status.${l.status}`)}</span>{' '}
                  {l.decision === 'linked' ? t('orgs.links.primaryOf', { primary: l.primary.displayName, other: l.other.displayName }) : `${l.primary.displayName} · ${l.other.displayName}`}
                  <small>
                    {t('orgs.links.by', { who: l.decidedBy ?? t('common.none'), when: dateTime(l.decidedAt) })} — {l.reason}
                    {l.status === 'reversed' ? ` · ${t('orgs.links.reversedBy', { who: l.reversedBy ?? t('common.none'), when: dateTime(l.reversedAt!) })} — ${l.reverseReason ?? ''}` : ''}
                  </small>
                  {l.status === 'active' && links.data.canDecide ? (
                    <button className="button small quiet" type="button" onClick={() => setReversing(l)}>
                      {t('orgs.links.reverse')}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
      {review && matches.data ? <LinkReviewDialog personId={personId} personVersion={matches.data.person.version} match={review} onClose={() => setReview(null)} /> : null}
      {reversing ? <ReverseLinkDialog link={reversing} personId={personId} onClose={() => setReversing(null)} /> : null}
    </section>
  );
}

function LinkReviewDialog({ personId, personVersion, match, onClose }: { personId: string; personVersion: number; match: PossibleMatchesResponse['items'][number]; onClose: () => void }) {
  const { t, tk } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const key = useKey();
  const [decision, setDecision] = useState<'linked' | 'kept_separate'>('kept_separate');
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api<PersonLinkDto>('POST', `/api/v1/people/${personId}/links`, { otherPersonId: match.id, decision, reason, expectedVersion: personVersion, otherExpectedVersion: match.version }, { idempotencyKey: key }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...scopeKey, 'person'] });
      onClose();
    },
  });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  return (
    <Dialog open title={t('orgs.links.reviewTitle')} subtitle={match.displayName} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={{ reason: 'link-reason' }} />
        <p className="tiny">{t('orgs.links.reasonsFound', { reasons: match.reasons.map((r) => tk(`orgs.links.reason.${r}`)).join(', ') || t('common.none') })}</p>
        <fieldset className="field full">
          <legend>{t('orgs.links.decisionLabel')}</legend>
          <label>
            <input type="radio" name="decision" checked={decision === 'kept_separate'} onChange={() => setDecision('kept_separate')} /> {t('orgs.links.keepSeparate')}
          </label>
          <label>
            <input type="radio" name="decision" checked={decision === 'linked'} onChange={() => setDecision('linked')} /> {t('orgs.links.link')}
          </label>
          <small>{decision === 'linked' ? t('orgs.links.linkHint') : t('orgs.links.keepHint')}</small>
        </fieldset>
        <Field id="link-reason" label={t('common.reason')} hint={t('orgs.links.reasonHint')} error={fe.reason}>
          {(p) => <textarea {...p} rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending || reason.trim().length < 3}>
            {t('orgs.links.record')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ReverseLinkDialog({ link, personId, onClose }: { link: PersonLinkDto; personId: string; onClose: () => void }) {
  const { t } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api<PersonLinkDto>('POST', `/api/v1/person-links/${link.id}/reverse`, { reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...scopeKey, 'person', personId] });
      onClose();
    },
  });
  return (
    <Dialog open title={t('orgs.links.reverseTitle')} subtitle={`${link.primary.displayName} · ${link.other.displayName}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={{ reason: 'reverse-reason' }} />
        <p className="tiny">{t('orgs.links.reverseHint')}</p>
        <Field id="reverse-reason" label={t('common.reason')} error={m.error instanceof ApiRequestError ? m.error.fields.reason : undefined}>
          {(p) => <textarea {...p} rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending || reason.trim().length < 3}>
            {t('orgs.links.reverse')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ------------------------------------------------------------------ affiliations (PB-03-03)
export function AffiliationsTable({ items, showPerson, showOrganisation, canEnd, onEnd }: { items: readonly AffiliationDto[]; showPerson: boolean; showOrganisation: boolean; canEnd: boolean; onEnd: (a: AffiliationDto) => void }) {
  const { t, tk, date } = useI18n();
  return (
    <div className="tablewrap" tabIndex={0} aria-label={t('orgs.aff.title')}>
      <table>
        <thead>
          <tr>
            {showPerson ? <th scope="col">{t('visit.col.person')}</th> : null}
            {showOrganisation ? <th scope="col">{t('orgs.col.organisation')}</th> : null}
            <th scope="col">{t('orgs.aff.role')}</th>
            <th scope="col">{t('orgs.aff.period')}</th>
            <th scope="col">{t('orgs.aff.source')}</th>
            {canEnd ? <th scope="col">{t('users.col.action')}</th> : null}
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={`${a.source}:${a.id}`}>
              {showPerson ? <td>{a.person ? <Link to={`/people/${a.person.id}`}>{a.person.displayName}</Link> : '—'}</td> : null}
              {showOrganisation ? (
                <td>
                  <Link to={`/people/organisations/${a.organisation.id}`}>{a.organisation.name}</Link>
                </td>
              ) : null}
              <td>{a.roleTitle ?? '—'}</td>
              <td>
                {date(a.startsOn)} – {a.endsOn ? date(a.endsOn) : t('orgs.aff.open')}
                <small>{a.current ? t('orgs.aff.current') : t('orgs.aff.past')}</small>
                {a.endReason ? <small>{a.endReason}</small> : null}
              </td>
              <td>{tk(`orgs.aff.source.${a.source}`)}</td>
              {canEnd ? (
                <td>
                  {a.source === 'register' && a.current ? (
                    <button className="button small quiet" type="button" onClick={() => onEnd(a)}>
                      {t('orgs.aff.end')}
                    </button>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PersonAffiliationsPanel({ personId }: { personId: string }) {
  const { t } = useI18n();
  const { scopeKey, canAnywhere } = useSession();
  const q = useQuery({ queryKey: [...scopeKey, 'person', personId, 'affiliations'], queryFn: () => get<AffiliationListResponse>(`/api/v1/people/${personId}/affiliations`) });
  const [adding, setAdding] = useState(false);
  const [ending, setEnding] = useState<AffiliationDto | null>(null);
  const manage = canAnywhere('affiliation.manage');
  return (
    <section className="panel" aria-labelledby="orgs-aff">
      <div className="panel-head">
        <div>
          <h2 id="orgs-aff">{t('orgs.aff.title')}</h2>
          <p>{t('orgs.aff.sub')}</p>
        </div>
        {manage ? (
          <button className="button small" type="button" onClick={() => setAdding(true)}>
            {t('orgs.aff.add')}
          </button>
        ) : null}
      </div>
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <div className="pad">
          <ErrorState error={q.error} />
        </div>
      ) : q.data.items.length === 0 ? (
        <div className="empty">
          <p>{t('orgs.aff.none')}</p>
        </div>
      ) : (
        <AffiliationsTable items={q.data.items} showPerson={false} showOrganisation canEnd={manage} onEnd={setEnding} />
      )}
      {adding ? <AddAffiliationDialog personId={personId} onClose={() => setAdding(false)} /> : null}
      {ending ? <EndAffiliationDialog affiliation={ending} onClose={() => setEnding(null)} /> : null}
    </section>
  );
}

function AddAffiliationDialog({ personId, onClose }: { personId: string; onClose: () => void }) {
  const { t } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [f, setF] = useState({ organisationId: '', roleTitle: '', startsOn: today(), endsOn: '' });
  const orgs = useQuery({ queryKey: [...scopeKey, 'organisations', 'pick', q], queryFn: () => get<{ items: OrganisationSummaryDto[] }>(`/api/v1/organisations?${new URLSearchParams({ q, limit: '20' })}`) });
  const m = useMutation({
    mutationFn: () => api<AffiliationDto>('POST', `/api/v1/people/${personId}/affiliations`, { organisationId: f.organisationId, roleTitle: f.roleTitle || null, startsOn: f.startsOn, endsOn: f.endsOn || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...scopeKey, 'person', personId] });
      onClose();
    },
  });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  return (
    <Dialog open title={t('orgs.aff.add')} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={{ organisationId: 'aff-org', startsOn: 'aff-start', endsOn: 'aff-end' }} />
        <div className="formgrid">
          <Field id="aff-search" label={t('orgs.search')}>
            {(p) => <input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
          </Field>
          <Field id="aff-org" label={t('orgs.col.organisation')} error={fe.organisationId}>
            {(p) => (
              <select {...p} value={f.organisationId} onChange={(e) => setF({ ...f, organisationId: e.target.value })}>
                <option value="">{t('common.none')}</option>
                {(orgs.data?.items ?? [])
                  .filter((o) => !o.contractorGoverned)
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
              </select>
            )}
          </Field>
          <Field id="aff-role" label={t('orgs.aff.role')}>
            {(p) => <input {...p} maxLength={120} value={f.roleTitle} onChange={(e) => setF({ ...f, roleTitle: e.target.value })} />}
          </Field>
          <Field id="aff-start" label={t('orgs.aff.startsOn')} error={fe.startsOn}>
            {(p) => <input {...p} type="date" value={f.startsOn} onChange={(e) => setF({ ...f, startsOn: e.target.value })} />}
          </Field>
          <Field id="aff-end" label={t('orgs.aff.endsOn')} hint={t('orgs.aff.endsOnHint')} error={fe.endsOn}>
            {(p) => <input {...p} type="date" value={f.endsOn} onChange={(e) => setF({ ...f, endsOn: e.target.value })} />}
          </Field>
        </div>
        <p className="tiny">{t('orgs.aff.contractorHint')}</p>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending || !f.organisationId || !f.startsOn}>
            {t('orgs.aff.add')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function EndAffiliationDialog({ affiliation, onClose }: { affiliation: AffiliationDto; onClose: () => void }) {
  const { t } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const [f, setF] = useState({ endsOn: today(), reason: '' });
  const m = useMutation({
    mutationFn: () => api<AffiliationDto>('POST', `/api/v1/affiliations/${affiliation.id}/end`, { expectedVersion: affiliation.version, endsOn: f.endsOn, reason: f.reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...scopeKey] });
      onClose();
    },
  });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  return (
    <Dialog open title={t('orgs.aff.end')} subtitle={affiliation.organisation.name} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={{ endsOn: 'aff-ends', reason: 'aff-reason' }} />
        <div className="formgrid">
          <Field id="aff-ends" label={t('orgs.aff.endsOn')} error={fe.endsOn}>
            {(p) => <input {...p} type="date" value={f.endsOn} onChange={(e) => setF({ ...f, endsOn: e.target.value })} />}
          </Field>
          <Field id="aff-reason" label={t('common.reason')} error={fe.reason}>
            {(p) => <input {...p} maxLength={500} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />}
          </Field>
        </div>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending || f.reason.trim().length < 3}>
            {t('orgs.aff.end')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ------------------------------------------------------------------ host reassignment (PB-03-02)
export function ReassignButton({ item }: { item: HostReassignmentDto }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="button small" type="button" onClick={() => setOpen(true)}>
        {t('orgs.ra.reassign')}
      </button>
      {open ? <ReassignDialog item={item} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function ReassignDialog({ item, onClose }: { item: HostReassignmentDto; onClose: () => void }) {
  const { t } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const key = useKey();
  const [f, setF] = useState({ q: '', toHostId: '', reason: '' });
  const [confirm, setConfirm] = useState(false);
  const hosts = useQuery({ queryKey: [...scopeKey, 'hosts', item.siteId, 'pick', f.q], queryFn: () => get<{ items: HostResponse[] }>(`/api/v1/hosts?${new URLSearchParams({ siteId: item.siteId, ...(f.q ? { q: f.q } : {}) })}`) });
  const m = useMutation({
    mutationFn: () => api<ReassignHostResponse>('POST', `/api/v1/host-reassignments/${item.id}/reassign`, { toHostId: f.toHostId, reason: f.reason, confirmImpact: confirm }, { idempotencyKey: key }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...scopeKey, 'host-reassignments'] });
      void qc.invalidateQueries({ queryKey: [...scopeKey, 'visit'] });
      onClose();
    },
  });
  const impact = m.error instanceof ApiRequestError && m.error.code === 'IMPACT_REVIEW_REQUIRED' ? Number(m.error.details.approvalsAffected ?? 0) : null;
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  return (
    <Dialog open title={t('orgs.ra.title')} subtitle={`${item.visit.reference} · ${item.host.displayName}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        {impact === null ? <ErrorSummary error={m.error} fieldIds={{ toHostId: 'ra-host', reason: 'ra-reason' }} /> : null}
        <p className="tiny">{t('orgs.ra.hint')}</p>
        <div className="formgrid">
          <Field id="ra-search" label={t('orgs.ra.search')}>
            {(p) => <input {...p} type="search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />}
          </Field>
          <Field id="ra-host" label={t('orgs.ra.newHost')} error={fe.toHostId}>
            {(p) => (
              <select {...p} value={f.toHostId} onChange={(e) => setF({ ...f, toHostId: e.target.value })}>
                <option value="">{t('common.none')}</option>
                {(hosts.data?.items ?? [])
                  .filter((h) => h.id !== item.host.id)
                  .map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.displayName}
                      {h.department ? ` · ${h.department}` : ''}
                    </option>
                  ))}
              </select>
            )}
          </Field>
          <Field id="ra-reason" label={t('common.reason')} error={fe.reason} full>
            {(p) => <input {...p} maxLength={500} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />}
          </Field>
        </div>
        {impact !== null ? (
          <div className="note warning" role="alert">
            <strong>{t('orgs.ra.impactTitle')}</strong>
            {t('orgs.ra.impact', { count: impact })}
            <label style={{ display: 'block', marginBlockStart: 8 }}>
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} /> {t('orgs.ra.confirm')}
            </label>
          </div>
        ) : null}
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending || !f.toHostId || f.reason.trim().length < 3 || (impact !== null && !confirm)}>
            {t('orgs.ra.reassign')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ------------------------------------------------------------------ roster import (PB-04-03)
const toBase64 = async (file: File) => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
};

const ROW_TONE: Record<RosterRowDto['status'], string> = { valid: 'green', added: 'green', invalid: 'red', failed: 'red', duplicate: 'amber', skipped: '' };

export function RosterImportButton({ visit }: { visit: VisitDetailResponse }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="button small" type="button" onClick={() => setOpen(true)}>
        {t('orgs.roster.open')}
      </button>
      {open ? <RosterDialog visit={visit} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function RosterDialog({ visit, onClose }: { visit: VisitDetailResponse; onClose: () => void }) {
  const { t, tk } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const [file, setFile] = useState<{ name: string; content: string } | null>(null);
  const [inspect, setInspect] = useState<InspectRosterResponse | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importId, setImportId] = useState<string | null>(null);
  const [policy, setPolicy] = useState<'valid_rows_only' | 'all_or_nothing'>('valid_rows_only');
  const [createKey] = useState(newIdempotencyKey);
  const [applyKey, setApplyKey] = useState(newIdempotencyKey);
  const [result, setResult] = useState<ApplyRosterResponse | null>(null);
  const base = `/api/v1/visits/${visit.id}/roster`;
  const inspectM = useMutation({ mutationFn: () => api<InspectRosterResponse>('POST', `${base}/inspect`, { fileName: file!.name, contentBase64: file!.content }), onSuccess: (r) => (setInspect(r), setMapping({ ...r.suggestedMapping })) });
  const stageM = useMutation({ mutationFn: () => api<RosterImportDto>('POST', base, { fileName: file!.name, contentBase64: file!.content, mapping }, { idempotencyKey: createKey }), onSuccess: (r) => setImportId(r.id) });
  const rows = useQuery({ queryKey: [...scopeKey, 'roster', importId], enabled: !!importId, queryFn: () => get<{ import: RosterImportDto; items: RosterRowDto[] }>(`${base}/${importId}/rows`) });
  const decide = useMutation({ mutationFn: (x: { row: number; decision: 'create' | 'use_existing' | 'skip'; targetId?: string }) => api<RosterRowDto>('POST', `${base}/${importId}/rows/${x.row}/decision`, { decision: x.decision, targetId: x.targetId ?? null }), onSuccess: () => void rows.refetch() });
  const apply = useMutation({
    mutationFn: () => api<ApplyRosterResponse>('POST', `${base}/${importId}/apply`, { expectedVersion: rows.data!.import.version, policy }, { idempotencyKey: applyKey }),
    onSuccess: (r) => {
      setResult(r);
      void rows.refetch();
      void qc.invalidateQueries({ queryKey: [...scopeKey, 'visit'] });
    },
    onError: () => setApplyKey(newIdempotencyKey()),
  });
  const imp = rows.data?.import;
  return (
    <Dialog open title={t('orgs.roster.title')} subtitle={visit.title ?? visit.reference} onClose={onClose}>
      <p className="tiny">{t('orgs.roster.intro')}</p>
      {!importId ? (
        <>
          <ErrorSummary error={inspectM.error ?? stageM.error} fieldIds={{}} />
          <Field id="roster-file" label={t('orgs.roster.file')} hint={t('orgs.roster.fileHint')}>
            {(p) => (
              <input
                {...p}
                type="file"
                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  setInspect(null);
                  setFile(f ? { name: f.name, content: await toBase64(f) } : null);
                }}
              />
            )}
          </Field>
          <div className="actions">
            <button className="button" type="button" disabled={!file || inspectM.isPending} onClick={() => inspectM.mutate()}>
              {t('orgs.roster.inspect')}
            </button>
          </div>
          {inspect ? (
            <>
              <h3 style={{ marginBlockStart: 16 }}>{t('orgs.roster.mapping', { count: inspect.rowCount })}</h3>
              {inspect.blockedHeaders.length ? <p className="note warning">{t('orgs.roster.blocked', { columns: inspect.blockedHeaders.join(', ') })}</p> : null}
              <div className="formgrid">
                {inspect.headers
                  .filter((h) => !inspect.blockedHeaders.includes(h))
                  .map((h, i) => (
                    <Field key={h} id={`roster-map-${i}`} label={h}>
                      {(p) => (
                        <select {...p} value={mapping[h] ?? ''} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}>
                          <option value="">{t('orgs.roster.ignore')}</option>
                          {inspect.fields.map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                              {f.required ? ` (${t('orgs.roster.required')})` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </Field>
                  ))}
              </div>
              <div className="form-actions">
                <button className="button" type="button" onClick={onClose}>
                  {t('common.cancel')}
                </button>
                <button className="button primary" type="button" disabled={stageM.isPending} onClick={() => stageM.mutate()}>
                  {t('orgs.roster.dryRun')}
                </button>
              </div>
            </>
          ) : null}
        </>
      ) : rows.isPending ? (
        <Loading />
      ) : rows.isError ? (
        <ErrorState error={rows.error} />
      ) : (
        <>
          <p>
            {t('orgs.roster.counts', { rows: imp!.counts.rows, valid: imp!.counts.valid, invalid: imp!.counts.invalid, duplicate: imp!.counts.duplicate, undecided: imp!.counts.undecided })}
            {imp!.status === 'applied' ? ` ${t('orgs.roster.applied', { added: imp!.counts.added, failed: imp!.counts.failed, skipped: imp!.counts.skipped })}` : ''}
          </p>
          <div className="tablewrap" tabIndex={0} aria-label={t('orgs.roster.report')}>
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('orgs.roster.row')}</th>
                  <th scope="col">{t('visit.col.person')}</th>
                  <th scope="col">{t('orgs.roster.status')}</th>
                  <th scope="col">{t('orgs.roster.detail')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.data.items.map((r) => (
                  <tr key={r.rowNumber}>
                    <td data-ltr>{r.rowNumber}</td>
                    <td>
                      {[r.data.givenName, r.data.familyName].filter(Boolean).join(' ') || (r.participationId ? t('orgs.roster.addedPerson') : '—')}
                      {r.data.email ? <small data-ltr>{r.data.email}</small> : null}
                    </td>
                    <td>
                      <span className={`badge ${ROW_TONE[r.status]}`}>{tk(`orgs.roster.st.${r.status}`)}</span>
                    </td>
                    <td>
                      {Object.entries(r.errors).map(([k, v]) => (
                        <small key={k} className="field-error">
                          {v}
                        </small>
                      ))}
                      {r.status === 'duplicate' && imp!.status === 'validated' ? (
                        <div className="actions">
                          {r.matches.map((m) => (
                            <span key={m.id}>
                              <small>
                                {m.label} ({m.reasons.map((x) => tk(`orgs.links.reason.${x}`, undefined, x)).join(', ')})
                              </small>
                              {!m.id.startsWith('row:') ? (
                                <button className="button small" type="button" aria-pressed={r.decision === 'use_existing' && r.decisionTarget === m.id} onClick={() => decide.mutate({ row: r.rowNumber, decision: 'use_existing', targetId: m.id })}>
                                  {t('orgs.roster.useExisting')}
                                </button>
                              ) : null}
                            </span>
                          ))}
                          <button className="button small" type="button" aria-pressed={r.decision === 'create'} onClick={() => decide.mutate({ row: r.rowNumber, decision: 'create' })}>
                            {t('orgs.roster.createSeparate')}
                          </button>
                          <button className="button small quiet" type="button" aria-pressed={r.decision === 'skip'} onClick={() => decide.mutate({ row: r.rowNumber, decision: 'skip' })}>
                            {t('orgs.roster.skip')}
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {imp!.status === 'validated' ? (
            <>
              <ErrorSummary error={apply.error ?? decide.error} fieldIds={{}} />
              <fieldset className="field full" style={{ marginBlockStart: 14 }}>
                <legend>{t('orgs.roster.policy')}</legend>
                <label>
                  <input type="radio" name="policy" checked={policy === 'valid_rows_only'} onChange={() => setPolicy('valid_rows_only')} /> {t('orgs.roster.validOnly')}
                </label>
                <label>
                  <input type="radio" name="policy" checked={policy === 'all_or_nothing'} onChange={() => setPolicy('all_or_nothing')} /> {t('orgs.roster.allOrNothing')}
                </label>
              </fieldset>
              <div className="form-actions">
                <button className="button" type="button" onClick={onClose}>
                  {t('common.cancel')}
                </button>
                <button className="button primary" type="button" disabled={apply.isPending} onClick={() => apply.mutate()}>
                  {t('orgs.roster.apply')}
                </button>
              </div>
            </>
          ) : (
            <div className="form-actions">
              {result ? <p className="tiny">{t('orgs.roster.done', { added: result.added, failed: result.failed, skipped: result.skipped })}</p> : null}
              <button className="button primary" type="button" onClick={onClose}>
                {t('common.close')}
              </button>
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}

// ------------------------------------------------------------------ collection matrix (PB-16-03)
type Rule = CollectionMatrixVersionDto['document']['rules'][number];

export function CollectionMatrixTab() {
  const { t, tk, dateTime } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [...scopeKey, 'collection-matrix'], queryFn: () => get<CollectionMatrixResponse>('/api/v1/collection-matrix') });
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [category, setCategory] = useState('*');
  const refresh = () => void qc.invalidateQueries({ queryKey: [...scopeKey] });
  const save = useMutation({
    mutationFn: () => api<CollectionMatrixVersionDto>('PUT', '/api/v1/collection-matrix/draft', { expectedVersion: q.data?.draft?.version ?? 0, document: { schema: 1, rules: rules ?? [] } }),
    onSuccess: () => (setRules(null), refresh()),
  });
  // B-31 (PD-615): publishing goes through a confirmation that names what changes and what becomes hidden.
  const [publishing, setPublishing] = useState(false);
  const discard = useMutation({ mutationFn: () => api('POST', '/api/v1/collection-matrix/draft/discard', { expectedVersion: q.data!.draft!.version }), onSuccess: () => (setRules(null), refresh()) });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} />;
  const data = q.data;
  const current = rules ?? (data.draft ?? data.published)?.document.rules ?? [];
  const ruleFor = (item: string) => current.find((r) => r.category === category && !r.purpose && r.item === item);
  const setMode = (item: string, mode: Mode | '') => {
    const others = current.filter((r) => !(r.category === category && !r.purpose && r.item === item));
    setRules(mode ? [...others, { category, purpose: null, item, mode }] : others);
  };
  const effective = data.effective[category] ?? {};
  const docs = category === '*' ? [] : (data.categories.find((c) => c.code === category)?.documents ?? []);
  const items = data.items.filter((i) => i.kind !== 'document' || docs.includes(i.item.slice(9)));
  const purposeRules = current.filter((r) => r.purpose);
  return (
    <section className="panel" aria-labelledby="cm-title">
      <div className="panel-head">
        <div>
          <h2 id="cm-title">{t('orgs.cm.title')}</h2>
          <p>{t('orgs.cm.intro')}</p>
        </div>
      </div>
      <div className="pad">
        <p className="tiny">
          {data.published ? t('orgs.cm.published', { n: data.published.number, when: dateTime(data.published.publishedAt!) }) : t('orgs.cm.nonePublished')}
          {data.draft ? ` · ${t('orgs.cm.draft', { n: data.draft.number })}` : ''}
        </p>
        <Field id="cm-category" label={t('orgs.cm.category')}>
          {(p) => (
            <select {...p} value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="*">{t('orgs.cm.allTypes')}</option>
              {data.categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <div className="tablewrap" tabIndex={0} aria-label={t('orgs.cm.title')}>
          <table>
            <thead>
              <tr>
                <th scope="col">{t('orgs.cm.item')}</th>
                <th scope="col">{t('orgs.cm.effective')}</th>
                <th scope="col">{t('orgs.cm.rule')}</th>
                <th scope="col">{t('orgs.cm.class')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const e = effective[i.item];
                const r = ruleFor(i.item);
                const choices: Mode[] = i.kind === 'document' ? ['disallowed'] : i.captureOnly ? ['optional', 'disallowed'] : ['required', 'optional', 'disallowed'];
                return (
                  <tr key={i.item}>
                    <td>
                      <strong>{i.label}</strong>
                      <small>{tk(`orgs.cm.kind.${i.kind}`)}{i.captureOnly ? ` · ${t('orgs.cm.captureOnly')}` : ''}</small>
                    </td>
                    <td>{e ? <span className={`badge ${MODE_TONE[e.mode]}`}>{tk(`orgs.mode.${e.mode}`)}</span> : '—'}{e ? <small>{tk(`orgs.cm.source.${e.source}`)}</small> : null}</td>
                    <td>
                      {data.canManage ? (
                        <select aria-label={`${t('orgs.cm.rule')}: ${i.label}`} value={r?.mode ?? ''} onChange={(ev) => setMode(i.item, ev.target.value as Mode | '')}>
                          <option value="">{t('orgs.cm.followDefault')}</option>
                          {choices.map((m) => (
                            <option key={m} value={m}>
                              {tk(`orgs.mode.${m}`)}
                            </option>
                          ))}
                        </select>
                      ) : r ? (
                        tk(`orgs.mode.${r.mode}`)
                      ) : (
                        t('orgs.cm.followDefault')
                      )}
                    </td>
                    <td>{i.recordClass ? <span data-ltr>{i.recordClass}</span> : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {purposeRules.length ? (
          <>
            <h3 style={{ marginBlockStart: 16 }}>{t('orgs.cm.purposeRules')}</h3>
            <ul className="problems">
              {purposeRules.map((r) => (
                <li key={`${r.category}|${r.purpose}|${r.item}`}>
                  <span data-ltr>
                    {r.category} · {r.purpose} · {r.item}
                  </span>{' '}
                  → {tk(`orgs.mode.${r.mode}`)}
                </li>
              ))}
            </ul>
          </>
        ) : null}
        <p className="tiny">{t('orgs.cm.howEnforced')}</p>
        {data.canManage ? (
          <>
            <ErrorSummary error={save.error ?? discard.error} fieldIds={{}} />
            <div className="actions">
              <button className="button" type="button" disabled={!rules || save.isPending} onClick={() => save.mutate()}>
                {t('orgs.cm.saveDraft')}
              </button>
              <button className="button primary" type="button" disabled={!data.draft || !!rules} onClick={() => setPublishing(true)}>
                {t('orgs.cm.publish')}
              </button>
              {data.draft ? (
                <button className="button quiet" type="button" disabled={discard.isPending} onClick={() => discard.mutate()}>
                  {t('orgs.cm.discard')}
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <p className="tiny">{t('orgs.cm.readOnly')}</p>
        )}
      </div>
      {publishing && data.draft ? <MatrixPublishDialog draftVersion={data.draft.version} onClose={() => setPublishing(false)} onDone={() => { setPublishing(false); refresh(); }} /> : null}
    </section>
  );
}
